import { ActionIcon, Alert, Button, Checkbox, CopyButton, Group, List, Modal, Popover, Select, Stack, Text, TextInput, Textarea, ThemeIcon } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertOctagon, IconCheck, IconCopy, IconQuestionMark } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { ApiError, api } from '../lib/api';
import { appConfig } from '../lib/auth';

// Что считать инцидентом - коротко, для подсказки «?»
export function IncidentHelp({ inline = false }: { inline?: boolean }) {
  return (
    <Stack gap={6}>
      <Text size="sm" fw={600}>Зачем эта кнопка</Text>
      <Text size="sm">Быстро сообщить в Security о том, что могло навредить компании. Лучше сообщить лишний раз: за сообщение никого не наказывают, а ранний сигнал снижает ущерб.</Text>
      <Text size="sm" fw={500}>Сообщайте, если:</Text>
      <List size="sm" spacing={2}>
        <List.Item>пришло подозрительное письмо или сообщение в мессенджере;</List.Item>
        <List.Item>вы перешли по ссылке, ввели пароль или открыли файл и засомневались;</List.Item>
        <List.Item>потеряли ноутбук, телефон или пропуск;</List.Item>
        <List.Item>отправили данные не тому адресату;</List.Item>
        <List.Item>видите незнакомые входы или запросы подтверждения в своей учётке.</List.Item>
      </List>
      <Text size="sm">Если вы ввели пароль на подозрительной странице - сначала смените его, потом сообщите.</Text>
      {!inline && <Text size="xs" c="dimmed">Подробнее - в разделе <Link to="/help">«Помощь»</Link>.</Text>}
    </Stack>
  );
}

function ContactBlock() {
  const contact = appConfig().securityContact;
  return (
    <Stack gap="xs">
      <Text size="sm">Войти не получается? Напишите в Security на адрес:</Text>
      <Group gap="xs">
        <Text fw={600}>{contact}</Text>
        <CopyButton value={contact}>
          {({ copied, copy }) => <Button size="xs" variant="light" color={copied ? 'teal' : 'blue'} leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />} onClick={copy}>{copied ? 'Скопировано' : 'Скопировать'}</Button>}
        </CopyButton>
      </Group>
    </Stack>
  );
}

function ReportForm({ onDone }: { onDone: () => void }) {
  const cats = useQuery({ queryKey: ['incident-categories'], queryFn: () => api.get<{ value: string; label: string }[]>('/me/incident-categories'), staleTime: Infinity });
  const [category, setCategory] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [occurredAt, setOccurredAt] = useState('');
  const [urgent, setUrgent] = useState(false);
  const send = useMutation({ mutationFn: () => api.post<{ id: number }>('/me/incidents', { category, description, occurredAt, urgent, pageUrl: location.pathname }) });

  if (send.data) {
    return (
      <Stack align="center" ta="center" py="md">
        <ThemeIcon size={56} radius="xl" color="teal" variant="light"><IconCheck size={30} /></ThemeIcon>
        <Text fw={600}>Сообщение #{send.data.id} отправлено в Security</Text>
        <Text size="sm" c="dimmed">Спасибо. С вами свяжутся, если понадобятся детали. Если ситуация срочная и вы ввели пароль на подозрительной странице - смените его прямо сейчас.</Text>
        <Button onClick={onDone}>Готово</Button>
      </Stack>
    );
  }
  return (
    <Stack>
      <Select label="Что случилось" placeholder="Выберите" data={cats.data ?? []} value={category} onChange={setCategory} allowDeselect={false} />
      <Textarea label="Опишите коротко" description="Что произошло, от кого было сообщение, что вы уже сделали. Пароли и коды не пишите." autosize minRows={3} value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
      <TextInput label="Когда" placeholder="например: сегодня около 14:30" value={occurredAt} onChange={(e) => setOccurredAt(e.currentTarget.value)} />
      <Checkbox label="Срочно: угроза продолжается прямо сейчас" checked={urgent} onChange={(e) => setUrgent(e.currentTarget.checked)} />
      {send.error && <Alert color="red">{(send.error as ApiError).message}</Alert>}
      <Group justify="flex-end">
        <Button variant="default" onClick={onDone}>Отмена</Button>
        <Button color="red" disabled={!category || description.trim().length < 5} loading={send.isPending} onClick={() => send.mutate()}>Отправить в Security</Button>
      </Group>
    </Stack>
  );
}

// INC-01: кнопка на каждой странице. После входа - форма в приложении, до входа - контакт Security
export function IncidentButton({ authed = true, compact = false }: { authed?: boolean; compact?: boolean }) {
  const [opened, { open, close }] = useDisclosure(false);
  return (
    <>
      <Group gap={4} wrap="nowrap">
        <Button variant="light" color="red" size="xs" leftSection={<IconAlertOctagon size={14} />} onClick={open}>{compact ? 'Инцидент' : 'Сообщить об инциденте'}</Button>
        <Popover width={340} position="bottom-end" withArrow shadow="md">
          <Popover.Target>
            <ActionIcon variant="subtle" color="gray" size="sm" radius="xl" aria-label="Что такое инцидент"><IconQuestionMark size={14} /></ActionIcon>
          </Popover.Target>
          <Popover.Dropdown><IncidentHelp inline={!authed} /></Popover.Dropdown>
        </Popover>
      </Group>
      <Modal opened={opened} onClose={close} title={<Group gap="xs"><IconAlertOctagon size={18} color="var(--mantine-color-red-6)" /><Text fw={600}>Сообщить об инциденте</Text></Group>} size="lg" centered>
        {authed ? <ReportForm key={String(opened)} onDone={close} /> : <ContactBlock />}
      </Modal>
    </>
  );
}
